/**
 * 애플리케이션 에러 타입 정의
 * 일관된 에러 처리를 위한 커스텀 에러 클래스들
 */

export type ErrorCode =
  | 'NOT_FOUND'
  | 'VALIDATION_ERROR'
  | 'AUTHENTICATION_ERROR'
  | 'AUTHORIZATION_ERROR'
  | 'NETWORK_ERROR'
  | 'STORAGE_ERROR'
  | 'UNKNOWN_ERROR';

export interface ErrorContext {
  [key: string]: unknown;
}

/**
 * 기본 애플리케이션 에러 클래스
 */
export class AppError extends Error {
  public readonly timestamp: Date;
  public readonly isOperational: boolean;

  constructor(
    public readonly code: ErrorCode,
    message: string,
    public readonly context?: ErrorContext,
    isOperational = true
  ) {
    super(message);
    this.name = 'AppError';
    this.timestamp = new Date();
    this.isOperational = isOperational;

    // Error 클래스 상속 시 prototype 체인 유지
    Object.setPrototypeOf(this, new.target.prototype);

    // 스택 트레이스 캡처 (V8 엔진 전용)
    const ErrorWithStackTrace = Error as typeof Error & {
      captureStackTrace?: (target: object, constructorOpt?: Function) => void;
    };
    if (typeof ErrorWithStackTrace.captureStackTrace === 'function') {
      ErrorWithStackTrace.captureStackTrace(this, this.constructor);
    }
  }

  toJSON() {
    return {
      name: this.name,
      code: this.code,
      message: this.message,
      context: this.context,
      timestamp: this.timestamp.toISOString(),
      stack: this.stack,
    };
  }
}

/**
 * 리소스를 찾을 수 없을 때 발생하는 에러
 */
export class NotFoundError extends AppError {
  constructor(resource: string, id?: string) {
    super(
      'NOT_FOUND',
      id ? `${resource}(${id})을(를) 찾을 수 없습니다.` : `${resource}을(를) 찾을 수 없습니다.`,
      { resource, id }
    );
    this.name = 'NotFoundError';
  }
}

/**
 * 입력값 유효성 검사 실패 시 발생하는 에러
 */
export class ValidationError extends AppError {
  constructor(
    public readonly errors: Record<string, string>,
    message = '입력값이 유효하지 않습니다.'
  ) {
    super('VALIDATION_ERROR', message, { errors });
    this.name = 'ValidationError';
  }

  /**
   * 특정 필드의 에러 메시지 반환
   */
  getFieldError(field: string): string | undefined {
    return this.errors[field];
  }

  /**
   * 에러가 있는 필드 목록 반환
   */
  getErrorFields(): string[] {
    return Object.keys(this.errors);
  }
}

/**
 * 인증 실패 시 발생하는 에러
 */
export class AuthenticationError extends AppError {
  constructor(message = '인증에 실패했습니다.', context?: ErrorContext) {
    super('AUTHENTICATION_ERROR', message, context);
    this.name = 'AuthenticationError';
  }
}

/**
 * 권한 부족 시 발생하는 에러
 */
export class AuthorizationError extends AppError {
  constructor(
    action?: string,
    resource?: string,
    message = '이 작업을 수행할 권한이 없습니다.'
  ) {
    super('AUTHORIZATION_ERROR', message, { action, resource });
    this.name = 'AuthorizationError';
  }
}

/**
 * 네트워크 요청 실패 시 발생하는 에러
 */
export class NetworkError extends AppError {
  constructor(
    message = '네트워크 요청에 실패했습니다.',
    public readonly originalError?: Error
  ) {
    super('NETWORK_ERROR', message, {
      originalMessage: originalError?.message,
    });
    this.name = 'NetworkError';
  }
}

/**
 * 스토리지 작업 실패 시 발생하는 에러
 */
export class StorageError extends AppError {
  constructor(
    operation: 'upload' | 'download' | 'delete' | 'list',
    message?: string,
    context?: ErrorContext
  ) {
    const defaultMessages = {
      upload: '파일 업로드에 실패했습니다.',
      download: '파일 다운로드에 실패했습니다.',
      delete: '파일 삭제에 실패했습니다.',
      list: '파일 목록을 불러오는데 실패했습니다.',
    };

    super('STORAGE_ERROR', message || defaultMessages[operation], {
      operation,
      ...context,
    });
    this.name = 'StorageError';
  }
}

/**
 * Firebase 에러를 AppError로 변환
 */
export function fromFirebaseError(error: unknown): AppError {
  if (error instanceof AppError) {
    return error;
  }

  const firebaseError = error as { code?: string; message?: string };
  const code = firebaseError.code || '';

  // Firebase Auth 에러 매핑
  if (code.startsWith('auth/')) {
    const authMessages: Record<string, string> = {
      'auth/user-not-found': '등록되지 않은 사용자입니다.',
      'auth/wrong-password': '비밀번호가 올바르지 않습니다.',
      'auth/email-already-in-use': '이미 사용 중인 이메일입니다.',
      'auth/weak-password': '비밀번호가 너무 약합니다.',
      'auth/invalid-email': '이메일 형식이 올바르지 않습니다.',
      'auth/user-disabled': '비활성화된 계정입니다.',
      'auth/too-many-requests': '잠시 후 다시 시도해 주세요.',
      'auth/network-request-failed': '네트워크 연결을 확인해 주세요.',
    };

    return new AuthenticationError(
      authMessages[code] || '인증 중 오류가 발생했습니다.',
      { firebaseCode: code }
    );
  }

  // Firestore 에러 매핑
  if (code.startsWith('firestore/') || code === 'permission-denied') {
    if (code === 'permission-denied') {
      return new AuthorizationError();
    }
    return new AppError('UNKNOWN_ERROR', '데이터베이스 오류가 발생했습니다.', {
      firebaseCode: code,
    });
  }

  // Storage 에러 매핑
  if (code.startsWith('storage/')) {
    const storageMessages: Record<string, string> = {
      'storage/unauthorized': '파일 접근 권한이 없습니다.',
      'storage/canceled': '업로드가 취소되었습니다.',
      'storage/unknown': '알 수 없는 스토리지 오류가 발생했습니다.',
      'storage/object-not-found': '파일을 찾을 수 없습니다.',
      'storage/quota-exceeded': '스토리지 용량이 초과되었습니다.',
    };

    return new StorageError(
      'upload',
      storageMessages[code] || '스토리지 오류가 발생했습니다.',
      { firebaseCode: code }
    );
  }

  // 기타 에러
  return new AppError(
    'UNKNOWN_ERROR',
    firebaseError.message || '알 수 없는 오류가 발생했습니다.',
    { originalError: error }
  );
}

/**
 * 에러가 재시도 가능한지 확인
 */
export function isRetryableError(error: AppError): boolean {
  return error.code === 'NETWORK_ERROR' || error.code === 'UNKNOWN_ERROR';
}

/**
 * 에러가 사용자에게 표시해도 안전한지 확인
 */
export function isUserFacingError(error: AppError): boolean {
  return error.isOperational;
}
